import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme, Button } from '../../../../ui';
import { toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';

const WidgetContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const DiplomasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DiplomaCard = styled(Flex)`
  background: ${(props) => {
    return `linear-gradient(135deg, ${toRgba(props.$brandColor, 0.05)} 0%, ${toRgba(props.$brandColor, 0.08)} 100%)`;
  }};
  border: 2px solid
    ${(props) => {
      return toRgba(props.$brandColor, 0.25);
    }};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px ${toRgba(theme.colors.NEU_999, 0.1)};
  }
`;

const CertificateIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) => props.$brandColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px ${(props) => toRgba(props.$brandColor, 0.3)};
`;

const EmptyState = styled(Flex)`
  padding: 40px 20px;
  text-align: center;
`;

/**
 * DiplomasWidget Component
 *
 * Displays diplomas/certificates acquired by completing courses.
 * Shows which courses have been completed and their associated certificates.
 *
 * Features:
 * - Displays certificate information (course title, instructor, issue date, score)
 * - Links to view/download certificates
 * - Empty state when no certificates are available
 */
const DiplomasWidget = ({ certificates = [] }) => {
  if (!certificates || certificates.length === 0) {
    return (
      <WidgetContainer direction='column' align='flex-start'>
        <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
          <Flex direction='column' align='flex-start'>
            <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
              Diplomas & Certificates
            </Typo.ContentSmallHeader>
            <Typo.Info color={theme.colors.NEU_600}>Complete courses to earn certificates</Typo.Info>
          </Flex>
          <Icon.Certificate size={'medium'} color={theme.colors.MARKER} />
        </Flex>

        <EmptyState direction='column' align='center' width='100%'>
          <Icon.Certificate size={'large'} color={theme.colors.NEU_300} style={{ marginBottom: '16px' }} />
          <Typo.MediumText color={theme.colors.NEU_600}>No certificates yet</Typo.MediumText>
          <Typo.Info color={theme.colors.NEU_600} style={{ marginTop: '8px' }}>
            Complete courses to earn your first certificate
          </Typo.Info>
        </EmptyState>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer direction='column' align='flex-start'>
      <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
        <Flex direction='column' align='flex-start'>
          <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
            Diplomas & Certificates
          </Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>
            {certificates.length} {certificates.length === 1 ? 'certificate' : 'certificates'} earned
          </Typo.Info>
        </Flex>
        <Icon.Certificate size={'medium'} color={theme.colors.MARKER} />
      </Flex>

      <DiplomasGrid>
        {certificates.map((certificate) => {
          const issueDate = new Date(certificate.issuedDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });

          return (
            <DiplomaCard key={certificate.id} direction='column' align='flex-start' $brandColor={theme.colors.MARKER}>
              <CertificateIconWrapper $brandColor={theme.colors.MARKER}>
                <Icon.Certificate size={'medium'} color={theme.colors.NEU_0} />
              </CertificateIconWrapper>

              <Typo.TextTitle fontWeight='600' color={theme.colors.NEU_PRIMARY} style={{ marginBottom: '8px' }}>
                {certificate.courseTitle}
              </Typo.TextTitle>

              <Flex direction='column' gap='4' width='100%' style={{ marginBottom: '16px' }}>
                <Flex align='center' gap='8'>
                  <Icon.User size={'x-small'} color={theme.colors.NEU_600} />
                  <Typo.Info color={theme.colors.NEU_600}>{certificate.instructor}</Typo.Info>
                </Flex>

                <Flex align='center' gap='8'>
                  <Icon.Calendar size={'x-small'} color={theme.colors.NEU_600} />
                  <Typo.Info color={theme.colors.NEU_600}>Issued {issueDate}</Typo.Info>
                </Flex>

                {certificate.score && (
                  <Flex align='center' gap='8'>
                    <Icon.Certificate size={'x-small'} color={theme.colors.NEU_600} />
                    <Typo.Info color={theme.colors.NEU_600}>Score: {certificate.score}%</Typo.Info>
                  </Flex>
                )}
              </Flex>

              <Button
                size='small'
                variant='outlined'
                onClick={() => {
                  // TODO: Implement certificate viewing/download
                  console.log('View certificate:', certificate.id);
                }}
                style={{ width: '100%' }}
              >
                View Certificate
              </Button>
            </DiplomaCard>
          );
        })}
      </DiplomasGrid>
    </WidgetContainer>
  );
};

export default DiplomasWidget;
